<script>
/**
 * 打款凭据-附件上传（支持多文件：图片/PDF/Word/Excel 等）
 * 上传流程：el-upload -> /admin_api/upload/file -> /artist_divide/add_attachment
 */
import { mapGetters } from 'vuex'
import { getToken } from '@/utils/auth'

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp']

export default {
  name: 'UploadAttachment',
  props: {
    // 分成记录ID
    divideId: {
      type: [String, Number],
      default: null
    },
    // 附件列表（divide_attachments）
    attachments: {
      type: Array,
      default: () => []
    },
    // 单个附件最大体积（MB）
    maxSize: {
      type: Number,
      default: 50
    }
  },
  data() {
    return {
      uploadHeaders: { 'HYPERF-SESSION-ID': null },
      uploading: 0
    }
  },
  computed: {
    ...mapGetters(['userPermissions']),
    canEdit() {
      return this.userPermissions && this.userPermissions.indexOf('artist_divide_save') > -1
    },
    imageList() {
      return (this.attachments || []).filter(item => this.isImage(item)).map(item => item.full_path)
    }
  },
  methods: {
    isImage(row) {
      const path = (row && row.full_path) || ''
      const ext = (row && row.ext) || path.split('?')[0].split('.').pop()
      return IMAGE_EXT.indexOf(String(ext).toLowerCase()) > -1
    },
    sizeFormat(size) {
      const num = Number(size) || 0
      if (num <= 0) return '--'
      if (num < 1024) return num + ' B'
      if (num < 1024 * 1024) return (num / 1024).toFixed(1) + ' KB'
      return (num / 1024 / 1024).toFixed(2) + ' MB'
    },
    nameFormat(row) {
      return row.original || row.name || row.full_path
    },
    uploaderFormat(row) {
      if (!row.uploader) return '--'
      return row.uploader.nickname || row.uploader.username || '--'
    },
    beforeUpload(file) {
      if (!this.divideId) {
        this.$notify.error('分成记录不存在，无法上传附件')
        return false
      }
      if (file.size / 1024 / 1024 > this.maxSize) {
        this.$message.error(`单个附件大小不能超过 ${this.maxSize}MB`)
        return false
      }
      this.uploadHeaders['HYPERF-SESSION-ID'] = getToken()
      this.uploading += 1
      return true
    },
    async handleUploadSuccess(response, file) {
      this.uploading = this.uploading > 0 ? this.uploading - 1 : 0
      if (!response || response.code !== 200 || !response.data || response.data.length < 1) {
        this.$notify.error(`附件【${file.name}】上传失败：${(response && response.msg) || '上传接口返回异常'}`)
        return
      }
      const saved = await this.$api.addArtistDivideAttachment({
        id: this.divideId,
        attachments: response.data
      })
      if (saved) {
        this.$notify.success(`附件【${file.name}】上传成功`)
        this.$emit('saved')
      } else {
        this.$notify.error(`附件【${file.name}】保存失败`)
      }
    },
    handleUploadError(file) {
      this.uploading = this.uploading > 0 ? this.uploading - 1 : 0
      this.$notify.error(`附件【${(file && file.name) || ''}】上传失败`)
    },
    handleDelete(row) {
      this.$confirm(`确认删除附件【${this.nameFormat(row)}】吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const data = await this.$api.deleteArtistDivideAttachment(row.id)
        if (data) {
          this.$notify.success('附件已删除')
          this.$emit('saved')
        }
      }).catch(() => {})
    }
  }
}
</script>

<template>
  <div class='upload-attachment'>
    <div class='flex-sb attachment-header'>
      <h3>附件</h3>
      <el-upload action='/admin_api/upload/file'
                 name='file'
                 multiple
                 class='upload-demo'
                 :headers='uploadHeaders'
                 :show-file-list='false'
                 :before-upload='beforeUpload'
                 :on-success='handleUploadSuccess'
                 :on-error='handleUploadError'>
        <el-button type='primary' plain icon='el-icon-paperclip'
                   v-if='canEdit'
                   :disabled='uploading > 0'>
          {{ uploading > 0 ? `上传中(${uploading})` : '上传附件' }}
        </el-button>
      </el-upload>
    </div>
    <div class='default-table'>
      <el-table :data='attachments' size='mini' border>
        <el-table-column type='index' label='序号' width='60' align='center' />
        <el-table-column label='附件名称' min-width='260' show-overflow-tooltip>
          <template v-slot='{ row }'>
            <el-link type='primary' :href='row.full_path' target='_blank'>{{ nameFormat(row) }}</el-link>
          </template>
        </el-table-column>
        <el-table-column label='预览' width='110' align='center'>
          <template v-slot='{ row }'>
            <el-image v-if='isImage(row)'
                      :src='row.full_path'
                      :preview-src-list='imageList'
                      fit='cover'
                      style='width: 70px;height: 45px' />
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column label='大小' width='110' align='center'>
          <template v-slot='{ row }'>{{ sizeFormat(row.size) }}</template>
        </el-table-column>
        <el-table-column label='上传人' width='120' align='center'>
          <template v-slot='{ row }'>{{ uploaderFormat(row) }}</template>
        </el-table-column>
        <el-table-column label='上传时间' prop='created_at' width='170' align='center' />
        <el-table-column label='操作' width='140' align='center'>
          <template v-slot='{ row }'>
            <el-link type='text' :href='row.full_path' target='_blank' style="color: #ff3176">下载</el-link>
            <el-link type='text' v-if='canEdit' style='margin-left: 10px' @click='handleDelete(row)'>删除</el-link>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<style scoped>
.upload-attachment {
  padding: 10px 20px;
}

.upload-attachment .attachment-header {
  padding-bottom: 10px;
}
</style>
